import im from "immutable";

import { type TypeTagInfo, typeInfoFor } from "~/components/inspectorv2/traverseValue/TypeTagInfo";
import {
	type ValuePath,
	type ValuePathSlice,
	joinPath,
} from "~/components/inspectorv2/traverseValue/ValuePath";
import { iterateObjectValue } from "~/components/inspectorv2/traverseValue/iterateObjectValue";

export interface QueryableField {
	key: string;
	value: unknown;
	typeInfo: TypeTagInfo;
	path: ValuePathSlice;
}

/**
 * Takes a `ValuePath` to an `Array<unknown>` and returns all the fields that can be used in queries
 * that are interactively built up by the querybuilder.
 *
 * For example, suppose we have an array:
 *
 *     [{ a: 1, b: "hello" }, ...]
 *
 * Intuitively, we want querybuilder to allow users to (_e.g._) `orderby` field `a`, or `groupby`
 * field `b`, and so on. The goal of this function is to discover fields which the querybuilder can
 * use in queries.
 *
 * Returned from this function is a `Map` from the field paths to the `QueryableField`s. In this
 * case, it would be functionally similar to something like this:
 *
 *     im.Map([[".a", queryableField1], [".b", queryableField2]])
 */
export function enumerateQueryableFields(
	pathToCollection: ValuePath
): im.Map<string, QueryableField> {
	//
	// IMPORTANT: Passing in 1 object here greatly improves perf on nontrivial datasets.
	//
	// TODO: Find a better way to select an exemplar!
	//

	const collection = pathToCollection[pathToCollection.length - 1]?.value;
	if (!collection || !(collection instanceof Array)) {
		return im.Map();
	}

	const exemplar = collection[0];
	// const exemplarAccessorPathComponent = arrayElement(0, exemplar);

	const fields = [...iterateObjectValue(exemplar)].reduce((acc, [key, value]) => {
		if (typeof key === "symbol") {
			return acc;
		}

		const typeInfo = typeInfoFor(value);
		const newPath: ValuePathSlice = [
			{
				type: "ObjectField",
				key,
				value,
				typeInfo,
			},
		];
		return acc.set(joinPath(newPath), {
			key,
			value,
			typeInfo,
			path: newPath,
		});
	}, im.Map<string, QueryableField>());

	return fields;
}
